import React, { useState, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import axios from 'axios';
import { calculateUserAchievements } from '../../utils/achievementCalculation';
import AchievementNotification from '../../components/AchievementNotification/AchievementNotification';
import ProgressChart from '../../components/Dashboard/ProgressChart';
import GoalProgress from '../../components/Dashboard/GoalProgress';
import DashboardNudges from '../../components/Dashboard/DashboardNudges';
import NewAssessmentDetails from '../../components/PerformanceReviews/NewAssessmentDetails';
import { filterBySessionType, STREAK_MESSAGES, NO_STREAK_MESSAGES, hasRole, MANAGER_ROLES, EXECUTIVE_ROLES } from '../../pages/dashboardHelpers';
import GoalSettings from '../../components/Dashboard/GoalSettings';
import StatsOverview from './StatsOverview';
import TrainingCard from './TrainingCard';
import ResponseTrainingCard from './ResponseTrainingCard';
import DocumentUploadModal from '../../components/RecordPresentation/DocumentUploadModal';
import ProspectPrepLoadingModal from '../../components/RecordPresentation/ProspectPrepLoadingModal';
import {
  DashboardWrapper, WelcomeSection, WelcomeTitle, WelcomeSubtitle,
  StreakBadge, SectionTitle, TrainingGrid,
  FeedbackPanel, FeedbackHeader, FeedbackTitle, CloseButton, EmptyState,
  AchievementsSection, AchievementsGrid, AchievementCard,
  AchievementIcon, AchievementName, AchievementDesc, ShowMoreButton,
} from './DashboardPage.styles';

const DashboardPageV2 = observer(({ usersStore, companyStore, assessmentsStore, meetingStore, history }) => {
  const [expandedFeedbackSection, setExpandedFeedbackSection] = useState(null);
  const [availableAchievementsOpen, setAvailableAchievementsOpen] = useState(true);
  const [newAchievement, setNewAchievement] = useState(null);
  const [showAchievementNotification, setShowAchievementNotification] = useState(false);
  const [goalSettingsOpen, setGoalSettingsOpen] = useState(false);
  const [showDocUploadModal, setShowDocUploadModal] = useState(false);
  const [qaLoading, setQaLoading] = useState(false);
  const [prospectPrepLoading, setProspectPrepLoading] = useState(false);

  const startObjectionHandling = async () => {
    setQaLoading(true);
    try {
      await meetingStore.initQASession(companyStore.company.id, 5);
      history.push('/record-presentation/objection-handling');
    } catch (err) {
      console.error('Failed to start Q&A session:', err);
      setQaLoading(false);
    }
  };

  const startProspectPrep = async () => {
    setProspectPrepLoading(true);
    try {
      await meetingStore.initProspectPrepSession(companyStore.company.id, 5);
      history.push('/record-presentation/prospect-prep');
    } catch (err) {
      console.error('Failed to start Prospect Prep session:', err);
      setProspectPrepLoading(false);
    }
  };

  const handleDocumentQA = async (file) => {
    const companyId = companyStore.company.id;
    const filename = `doc-qa-${Date.now()}-${file.name}`;
    const contentType = file.type || 'application/octet-stream';
    const headers = { 'Content-Type': contentType };

    const uploadResult = await axios.post(
      `/companies/${companyId}/uploads/${filename}`,
      {},
      { headers }
    );
    const uploadUrl = uploadResult.data.uploadUrl;
    const documentKey = uploadResult.data.key;

    await axios.put(uploadUrl, file, { headers, baseURL: '' });
    await meetingStore.initDocumentQASession(companyId, 5, documentKey);
    history.push('/record-presentation/deal-coaching');
  };

  const startRecording = async (sessionType, isInteractive = false) => {
    const recordingKeyMap = {
      'Elevator Pitch': isInteractive ? 'elevator_pitch_2' : 'elevator_pitch',
      'Casual Encounter': isInteractive ? 'casual_encounter_2' : 'casual_encounter',
      'Sales Presentation': isInteractive ? 'sales_presentation_2' : 'sales_presentation',
    };
    const recordingKey = recordingKeyMap[sessionType];
    if (recordingKey) {
      await meetingStore.setMeetingType(recordingKey, companyStore.company.id);
      history.push(`/record-presentation/${recordingKey}`);
    }
  };

  const currentUser = usersStore?.currentUser;
  const isStoreLoading = usersStore?.isLoading || assessmentsStore?.isLoading;
  const isDataReady = usersStore && currentUser && currentUser.userId;

  let currentUserStreakData = null;
  let isOnStreak = false;
  let userAssessments = [];

  try {
    if (usersStore?.getCurrentUserStreakStatus && assessmentsStore) {
      currentUserStreakData = usersStore.getCurrentUserStreakStatus(assessmentsStore);
    }
    if (usersStore?.isUserOnStreak && assessmentsStore && currentUser?.userId) {
      isOnStreak = usersStore.isUserOnStreak(currentUser.userId, assessmentsStore);
    }
    if (assessmentsStore?.closedAssessmentsForUser && currentUser?.userId) {
      userAssessments = assessmentsStore.closedAssessmentsForUser(currentUser.userId);
    }
  } catch (error) {
    console.error('Error loading user data:', error);
  }

  const achievements = useMemo(() => {
    return calculateUserAchievements(userAssessments || [], currentUserStreakData);
  }, [userAssessments, currentUserStreakData]);

  useEffect(() => {
    if (!achievements?.earned?.length || !currentUser?.userId) return;
    const previousEarnedIds = JSON.parse(localStorage.getItem(`earnedAchievements_${currentUser.userId}`)) || [];
    const currentEarnedIds = achievements.earned.map(a => a.id);
    const newlyEarned = achievements.earned.filter(a => !previousEarnedIds.includes(a.id));
    if (newlyEarned.length > 0) {
      setNewAchievement(newlyEarned[0]);
      setShowAchievementNotification(true);
      localStorage.setItem(`earnedAchievements_${currentUser.userId}`, JSON.stringify(currentEarnedIds));
    }
  }, [achievements, currentUser?.userId]);

  const streakMotivation = useMemo(() => STREAK_MESSAGES[Math.floor(Math.random() * STREAK_MESSAGES.length)], []);
  const noStreakMotivation = useMemo(() => NO_STREAK_MESSAGES[Math.floor(Math.random() * NO_STREAK_MESSAGES.length)], []);

  // Compute stats
  const totalSessions = userAssessments.length;
  const currentStreak = currentUserStreakData?.currentStreak || 0;
  const weeklyTarget = companyStore?.company?.practiceGoals?.weeklyTarget;
  const now = new Date();
  const weekStart = new Date(now);
  const dayOfWeek = now.getUTCDay();
  const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  weekStart.setUTCDate(now.getUTCDate() - mondayOffset);
  weekStart.setUTCHours(0, 0, 0, 0);
  const thisWeekCount = userAssessments.filter(a => new Date(a.completedDateTime) >= weekStart).length;
  const weeklyGoalPct = weeklyTarget > 0 ? Math.min(100, Math.round((thisWeekCount / weeklyTarget) * 100)) : null;
  const canSetGoals = hasRole(currentUser, MANAGER_ROLES) || hasRole(currentUser, EXECUTIVE_ROLES);

  // Response Training stats
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const computeResponseStats = (sessionType) => {
    const typeAssessments = filterBySessionType(userAssessments, sessionType);
    const total = typeAssessments.length;
    let statusText = 'Not Started';

    if (total > 0) {
      const sorted = [...typeAssessments].sort((a, b) => new Date(b.completedDateTime) - new Date(a.completedDateTime));
      const lastDate = new Date(sorted[0].completedDateTime);
      const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const lastMidnight = new Date(lastDate.getFullYear(), lastDate.getMonth(), lastDate.getDate());
      const daysDiff = Math.round((todayMidnight - lastMidnight) / (1000 * 60 * 60 * 24));
      if (daysDiff === 0) statusText = 'Completed Today';
      else if (daysDiff === 1) statusText = 'Completed Yesterday';
      else if (daysDiff <= 30) statusText = `${daysDiff} days ago`;
      else statusText = `${Math.floor(daysDiff / 30)}mo ago`;
    }

    const thisMonth = typeAssessments.filter(a => new Date(a.completedDateTime) >= monthStart).length;

    return { totalSessions: total, statusText, thisMonth };
  };

  const objectionStats = computeResponseStats('Objection Handling');
  const dealCoachingStats = computeResponseStats('Deal Coaching');
  const prospectPrepStats = computeResponseStats('Prospect Prep');

  if (isStoreLoading || !isDataReady) {
    return (
      <DashboardWrapper>
        <WelcomeSection>
          <WelcomeTitle>{isStoreLoading ? 'Loading...' : 'Loading user data...'}</WelcomeTitle>
        </WelcomeSection>
      </DashboardWrapper>
    );
  }

  return (
    <DashboardWrapper>
      <DashboardNudges
        assessments={userAssessments}
        streakData={currentUserStreakData || {}}
        goals={companyStore?.company?.practiceGoals}
        onStartRecording={(sessionType) => startRecording(sessionType || 'Elevator Pitch')}
      />

      <WelcomeSection>
        <WelcomeTitle>
          Welcome back, {currentUser.firstName}
          {currentStreak > 0 && (
            <StreakBadge $isOnStreak={isOnStreak}>
              {'\u{1F525}'} {currentStreak} week streak
            </StreakBadge>
          )}
        </WelcomeTitle>
        <WelcomeSubtitle>
          {isOnStreak ? streakMotivation : noStreakMotivation}
        </WelcomeSubtitle>
      </WelcomeSection>

      <StatsOverview
        totalSessions={totalSessions}
        currentStreak={currentStreak}
        weeklyGoalPct={weeklyGoalPct}
        canSetGoals={canSetGoals}
        onSetGoals={canSetGoals ? () => setGoalSettingsOpen(true) : undefined}
      />

      <SectionTitle>Pitch Training</SectionTitle>
      <TrainingGrid data-tour="training-grid">
        {['Elevator Pitch', 'Casual Encounter', 'Sales Presentation'].map((sessionType, idx) => {
          const typeAssessments = filterBySessionType(userAssessments, sessionType);
          const lastCompleted = typeAssessments.length > 0
            ? [...typeAssessments].sort((a, b) => new Date(b.completedDateTime) - new Date(a.completedDateTime))[0]
            : null;
          const lastCompletedDate = lastCompleted ? new Date(lastCompleted.completedDateTime) : null;

          let standardCount = 0;
          let interactiveCount = 0;
          typeAssessments.forEach(a => {
            if (a.recordingKey && a.recordingKey.endsWith('_2')) {
              interactiveCount++;
            } else {
              standardCount++;
            }
          });

          let statusText = 'Not Started';
          if (lastCompleted && lastCompletedDate) {
            const todayMid = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const lastMid = new Date(lastCompletedDate.getFullYear(), lastCompletedDate.getMonth(), lastCompletedDate.getDate());
            const daysDiff = Math.round((todayMid - lastMid) / (1000 * 60 * 60 * 24));
            if (daysDiff === 0) statusText = 'Completed Today';
            else if (daysDiff === 1) statusText = 'Completed Yesterday';
            else if (daysDiff <= 30) statusText = `${daysDiff} days ago`;
            else statusText = `${Math.floor(daysDiff / 30)}mo ago`;
          }

          const card = (
            <TrainingCard
              key={sessionType}
              sessionType={sessionType}
              totalPractices={typeAssessments.length}
              standardCount={standardCount}
              interactiveCount={interactiveCount}
              statusText={statusText}
              userAssessments={userAssessments}
              onRecordStandard={() => startRecording(sessionType, false)}
              onRecordInteractive={() => startRecording(sessionType, true)}
              onViewFeedback={() => setExpandedFeedbackSection(
                expandedFeedbackSection === sessionType ? null : sessionType
              )}
              isExpanded={expandedFeedbackSection === sessionType}
            />
          );
          if (idx === 0) return <div key={sessionType} data-tour="training-card-first" style={{display: 'flex'}}>{card}</div>;
          return card;
        })}
      </TrainingGrid>

      {/* Pitch Feedback Section */}
      {expandedFeedbackSection && expandedFeedbackSection !== 'Objection Handling' && expandedFeedbackSection !== 'Deal Coaching' && (() => {
        const typeAssessments = filterBySessionType(userAssessments, expandedFeedbackSection)
          .sort((a, b) => new Date(b.completedDateTime) - new Date(a.completedDateTime));
        return (
          <FeedbackPanel>
            <FeedbackHeader>
              <FeedbackTitle>{expandedFeedbackSection} Feedback</FeedbackTitle>
              <CloseButton onClick={() => setExpandedFeedbackSection(null)}>
                {'\u2715'}
              </CloseButton>
            </FeedbackHeader>
            {typeAssessments.length === 0 ? (
              <EmptyState>
                No {expandedFeedbackSection.toLowerCase()} assessments completed yet.
                Complete your first assessment to see feedback here.
              </EmptyState>
            ) : (
              <NewAssessmentDetails assessments={typeAssessments} />
            )}
          </FeedbackPanel>
        );
      })()}

      <SectionTitle>Response Training</SectionTitle>
      <TrainingGrid data-tour="response-training">
        {/* Prospect Prep — hidden until ready for release */}
        {false && <ResponseTrainingCard
          sessionType="Prospect Prep"
          totalSessions={prospectPrepStats.totalSessions}
          thisMonth={prospectPrepStats.thisMonth}
          statusText={prospectPrepStats.statusText}
          userAssessments={userAssessments}
          actionLabel="Start Practice"
          onAction={startProspectPrep}
          isLoading={prospectPrepLoading}
          onViewFeedback={() => setExpandedFeedbackSection(
            expandedFeedbackSection === 'Prospect Prep' ? null : 'Prospect Prep'
          )}
          isExpanded={expandedFeedbackSection === 'Prospect Prep'}
        />}
        <ResponseTrainingCard
          sessionType="Objection Handling"
          totalSessions={objectionStats.totalSessions}
          thisMonth={objectionStats.thisMonth}
          statusText={objectionStats.statusText}
          userAssessments={userAssessments}
          actionLabel="Start Practice"
          onAction={startObjectionHandling}
          isLoading={qaLoading}
          onViewFeedback={() => setExpandedFeedbackSection(
            expandedFeedbackSection === 'Objection Handling' ? null : 'Objection Handling'
          )}
          isExpanded={expandedFeedbackSection === 'Objection Handling'}
        />
        <ResponseTrainingCard
          sessionType="Deal Coaching"
          totalSessions={dealCoachingStats.totalSessions}
          thisMonth={dealCoachingStats.thisMonth}
          statusText={dealCoachingStats.statusText}
          userAssessments={userAssessments}
          actionLabel="Upload Document"
          onAction={() => setShowDocUploadModal(true)}
          isLoading={false}
          onViewFeedback={() => setExpandedFeedbackSection(
            expandedFeedbackSection === 'Deal Coaching' ? null : 'Deal Coaching'
          )}
          isExpanded={expandedFeedbackSection === 'Deal Coaching'}
        />
      </TrainingGrid>

      {/* Response Training Feedback */}
      {(expandedFeedbackSection === 'Objection Handling' || expandedFeedbackSection === 'Deal Coaching' || expandedFeedbackSection === 'Prospect Prep') && (() => {
        const typeAssessments = filterBySessionType(userAssessments, expandedFeedbackSection)
          .sort((a, b) => new Date(b.completedDateTime) - new Date(a.completedDateTime));
        return (
          <FeedbackPanel>
            <FeedbackHeader>
              <FeedbackTitle>{expandedFeedbackSection} Feedback</FeedbackTitle>
              <CloseButton onClick={() => setExpandedFeedbackSection(null)}>
                {'\u2715'}
              </CloseButton>
            </FeedbackHeader>
            {typeAssessments.length === 0 ? (
              <EmptyState>
                No {expandedFeedbackSection.toLowerCase()} assessments completed yet.
                Complete your first assessment to see feedback here.
              </EmptyState>
            ) : (
              <NewAssessmentDetails
                assessments={typeAssessments}
                canEditLibrary={!usersStore.currentUser.isReadOnly}
                onSaveToLibrary={expandedFeedbackSection === 'Prospect Prep' ? async (questionsToSave) => {
                  const existing = companyStore.company.questionsAndAnswers.map(qa => ({
                    question: qa.question,
                    answer: qa.answer,
                    related_to: qa.related_to,
                    ...(qa.uploadKey ? { uploadKey: qa.uploadKey } : {})
                  }));
                  await companyStore.company.updateQuestionsAndAnswers([...existing, ...questionsToSave]);
                } : undefined}
              />
            )}
          </FeedbackPanel>
        );
      })()}

      <GoalProgress assessments={userAssessments} companyStore={companyStore} />
      <ProgressChart assessments={userAssessments} />

      {/* Achievements */}
      <AchievementsSection>
        <SectionTitle>Achievements ({achievements.totalEarned})</SectionTitle>
        <AchievementsGrid>
          {achievements.earned.map((achievement) => (
            <AchievementCard key={achievement.id} $earned $color={achievement.color}>
              <AchievementIcon $color={achievement.color} $earned>
                {achievement.icon}
              </AchievementIcon>
              <AchievementName $color={achievement.color} $earned>
                {achievement.name}
              </AchievementName>
              <AchievementDesc>{achievement.description}</AchievementDesc>
            </AchievementCard>
          ))}
        </AchievementsGrid>

        {achievements.available.length > 0 && (
          <>
            <ShowMoreButton onClick={() => setAvailableAchievementsOpen(!availableAchievementsOpen)}>
              {availableAchievementsOpen ? 'Hide' : 'Show'} Available ({achievements.totalAvailable})
            </ShowMoreButton>
            {availableAchievementsOpen && (
              <AchievementsGrid>
                {achievements.available.map((achievement) => (
                  <AchievementCard key={achievement.id} $color={achievement.color}>
                    <AchievementIcon $color={achievement.color}>
                      {achievement.icon}
                    </AchievementIcon>
                    <AchievementName $color={achievement.color}>
                      {achievement.name}
                    </AchievementName>
                    <AchievementDesc>{achievement.description}</AchievementDesc>
                  </AchievementCard>
                ))}
              </AchievementsGrid>
            )}
          </>
        )}
      </AchievementsSection>

      <AchievementNotification
        achievement={newAchievement}
        isVisible={showAchievementNotification}
        onClose={() => {
          setShowAchievementNotification(false);
          setNewAchievement(null);
        }}
      />

      {canSetGoals && (
        <GoalSettings
          companyStore={companyStore}
          open={goalSettingsOpen}
          onClose={() => setGoalSettingsOpen(false)}
        />
      )}

      <DocumentUploadModal
        open={showDocUploadModal}
        onClose={() => setShowDocUploadModal(false)}
        onDocumentQA={handleDocumentQA}
      />

      <ProspectPrepLoadingModal open={prospectPrepLoading} />
    </DashboardWrapper>
  );
});

export default DashboardPageV2;
